import { supabase } from '@/lib/supabase';

export interface Business {
  id: string;
  owner_id: string;
  business_name: string;
  business_number: string;
  representative_name: string;
  business_phone?: string;
  business_email?: string;
  headquarters_address?: string;
  is_verified: boolean;
  verification_date?: string;
  status: 'active' | 'suspended' | 'terminated';
  created_at: string;
  updated_at: string;
  restaurants?: Restaurant[];
}

export interface Restaurant {
  id: string;
  business_id: string;
  name: string;
  address: string;
  phone?: string;
  settlement_bank_name?: string;
  settlement_account_number?: string;
  settlement_account_holder?: string;
  is_open: boolean;
  rating: number;
  total_reviews: number;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBusinessRequest {
  business_name: string;
  business_number: string;
  representative_name: string;
  business_phone?: string;
  business_email?: string;
  headquarters_address?: string;
}

export interface CreateRestaurantRequest {
  business_id: string;
  name: string;
  address: string;
  phone?: string;
  settlement_bank_name?: string;
  settlement_account_number?: string;
  settlement_account_holder?: string;
  latitude?: number;
  longitude?: number;
  image_url?: string;
  description?: string;
  store_type?: string;
}

class BusinessService {

  async createBusiness(
    ownerId: string,
    businessData: CreateBusinessRequest
  ): Promise<{ success: boolean; error?: string; business?: Business }> {
    try {
      // Check if business number already exists
      const { data: existingBusiness } = await supabase
        .from('businesses')
        .select('id')
        .eq('business_number', businessData.business_number)
        .single();

      if (existingBusiness) {
        return { success: false, error: '이미 등록된 사업자등록번호입니다.' };
      }

      const { data, error } = await supabase
        .from('businesses')
        .insert({
          owner_id: ownerId,
          ...businessData,
          status: 'active'
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, business: data };
    } catch (error) {
      console.error('Error creating business:', error);
      return { success: false, error: '사업자 등록에 실패했습니다.' };
    }
  }

  async getBusinessByOwner(ownerId: string): Promise<Business[]> {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          *,
          restaurants(*)
        `)
        .eq('owner_id', ownerId)
        .eq('status', 'active');

      if (error) {
        console.error('Error fetching businesses:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching businesses:', error);
      return [];
    }
  }

  async getBusinessById(businessId: string): Promise<Business | null> {
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          *,
          restaurants(*)
        `)
        .eq('id', businessId)
        .single();

      if (error || !data) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching business:', error);
      return null;
    }
  }

  async updateBusiness(
    businessId: string,
    updates: Partial<CreateBusinessRequest>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', businessId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating business:', error);
      return { success: false, error: '사업자 정보 업데이트에 실패했습니다.' };
    }
  }

  async createRestaurant(
    restaurantData: CreateRestaurantRequest
  ): Promise<{ success: boolean; error?: string; restaurant?: Restaurant }> {
    try {
      // Verify business ownership
      const business = await this.getBusinessById(restaurantData.business_id);
      if (!business) {
        return { success: false, error: '사업자 정보를 찾을 수 없습니다.' };
      }

      const { data, error } = await supabase
        .from('restaurants')
        .insert({
          business_id: restaurantData.business_id,
          name: restaurantData.name,
          address: restaurantData.address,
          phone: restaurantData.phone,
          settlement_bank_name: restaurantData.settlement_bank_name,
          settlement_account_number: restaurantData.settlement_account_number,
          settlement_account_holder: restaurantData.settlement_account_holder,
          latitude: restaurantData.latitude,
          longitude: restaurantData.longitude,
          image_url: restaurantData.image_url,
          description: restaurantData.description,
          store_type: restaurantData.store_type || 'korean',
          is_open: false, // Start closed, owner can open manually
          rating: 0,
          total_reviews: 0
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, restaurant: data };
    } catch (error) {
      console.error('Error creating restaurant:', error);
      return { success: false, error: '매장 등록에 실패했습니다.' };
    }
  }

  async getRestaurantsByBusiness(businessId: string): Promise<Restaurant[]> {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching restaurants:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      return [];
    }
  }

  async updateRestaurant(
    restaurantId: string,
    updates: Partial<CreateRestaurantRequest>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', restaurantId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating restaurant:', error);
      return { success: false, error: '매장 정보 업데이트에 실패했습니다.' };
    }
  }

  async toggleRestaurantStatus(
    restaurantId: string,
    isOpen: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({ 
          is_open: isOpen,
          updated_at: new Date().toISOString()
        })
        .eq('id', restaurantId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error toggling restaurant status:', error);
      return { success: false, error: '매장 상태 변경에 실패했습니다.' };
    }
  }

  async verifyBusiness(
    businessId: string,
    verified: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: any = {
        is_verified: verified,
        updated_at: new Date().toISOString()
      };

      if (verified) {
        updateData.verification_date = new Date().toISOString();
      }

      const { error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error verifying business:', error);
      return { success: false, error: '사업자 인증에 실패했습니다.' };
    }
  }

  async getBusinessStats(businessId: string): Promise<{
    totalRestaurants: number;
    activeRestaurants: number;
    totalOrders: number;
    totalRevenue: number;
    thisMonthRevenue: number;
  }> {
    try {
      // Get restaurants count
      const { data: restaurants } = await supabase
        .from('restaurants')
        .select('id, is_open')
        .eq('business_id', businessId);

      const totalRestaurants = restaurants?.length || 0;
      const activeRestaurants = restaurants?.filter(r => r.is_open).length || 0;

      if (totalRestaurants === 0) {
        return {
          totalRestaurants: 0,
          activeRestaurants: 0,
          totalOrders: 0,
          totalRevenue: 0,
          thisMonthRevenue: 0
        };
      }

      const restaurantIds = restaurants!.map(r => r.id);

      // Get orders stats
      const { data: orders } = await supabase
        .from('orders')
        .select('total_amount, created_at')
        .in('restaurant_id', restaurantIds)
        .eq('status', 'COMPLETED');

      const totalOrders = orders?.length || 0;
      const totalRevenue = orders?.reduce((sum, order) => sum + order.total_amount, 0) || 0;

      // Calculate this month's revenue
      const thisMonthStart = new Date();
      thisMonthStart.setDate(1);
      thisMonthStart.setHours(0, 0, 0, 0);

      const thisMonthRevenue = orders
        ?.filter(order => new Date(order.created_at) >= thisMonthStart)
        .reduce((sum, order) => sum + order.total_amount, 0) || 0;

      return {
        totalRestaurants,
        activeRestaurants,
        totalOrders,
        totalRevenue,
        thisMonthRevenue
      };
    } catch (error) {
      console.error('Error fetching business stats:', error);
      return {
        totalRestaurants: 0,
        activeRestaurants: 0,
        totalOrders: 0,
        totalRevenue: 0,
        thisMonthRevenue: 0
      };
    }
  }

  async migrateExistingRestaurants(): Promise<{ success: boolean; migrated: number; error?: string }> {
    try {
      // Find restaurants without business_id that have owner_id
      const { data: restaurantsToMigrate } = await supabase
        .from('restaurants')
        .select('id, owner_id, name, business_license, business_name, ceo_name')
        .is('business_id', null)
        .not('owner_id', 'is', null);

      if (!restaurantsToMigrate || restaurantsToMigrate.length === 0) {
        return { success: true, migrated: 0 };
      }

      let migratedCount = 0;

      for (const restaurant of restaurantsToMigrate) {
        try {
          // Create business for this restaurant
          const businessData = {
            owner_id: restaurant.owner_id,
            business_name: restaurant.business_name || restaurant.name,
            business_number: restaurant.business_license || `temp-${restaurant.id}`,
            representative_name: restaurant.ceo_name || 'Unknown',
            status: 'active',
            is_verified: false
          };

          const { data: business, error: businessError } = await supabase
            .from('businesses')
            .insert(businessData)
            .select()
            .single();

          if (businessError) {
            console.error(`Error creating business for restaurant ${restaurant.id}:`, businessError);
            continue;
          }

          // Update restaurant with business_id
          const { error: updateError } = await supabase
            .from('restaurants')
            .update({ business_id: business.id })
            .eq('id', restaurant.id);

          if (updateError) {
            console.error(`Error updating restaurant ${restaurant.id}:`, updateError);
            continue;
          }

          migratedCount++;
        } catch (err) {
          console.error(`Error migrating restaurant ${restaurant.id}:`, err);
        }
      }

      return { success: true, migrated: migratedCount };
    } catch (error) {
      console.error('Error during restaurant migration:', error);
      return { success: false, migrated: 0, error: '마이그레이션 중 오류가 발생했습니다.' };
    }
  }
}

export const businessService = new BusinessService();